import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

// Role permissions for UI visibility
// admin (Chris): full access, sees everything
// office (Alex, Amanda): finance, titles, vehicles, messaging, NO shop/recon details
// shop (Tony): inspections, work orders, parts, messaging, NO financials
// detail (Allen): his task queue only, minimal access
const ROLE_PERMISSIONS = {
  admin: {
    vehicles: ['read', 'write', 'delete', 'read_financials'],
    expenses: ['read', 'write', 'delete'],
    vendors: ['read', 'write', 'delete'],
    users: ['read', 'write', 'delete'],
    titles: ['read', 'write'],
    inspections: ['read', 'write'],
    parts: ['read', 'write', 'delete'],
    tasks: ['read', 'write', 'delete', 'assign'],
    reports: ['read'],
    financials: ['read', 'write'],
    recon: ['read', 'write'],
    messages: ['read', 'write'],
  },
  office: {
    vehicles: ['read', 'write', 'read_financials'], // Can see prices
    expenses: ['read', 'write'],
    vendors: ['read', 'write'],
    users: ['read'],
    titles: ['read', 'write'],
    inspections: [], // NO shop/recon details
    parts: [], // NO shop/recon details
    tasks: ['read', 'write'],
    reports: ['read'],
    financials: ['read', 'write'],
    recon: [], // NO recon access
    messages: ['read', 'write'],
  },
  shop: {
    vehicles: ['read'], // NO financials - can't see prices/profit
    expenses: [],
    vendors: ['read'],
    users: [],
    titles: [],
    inspections: ['read', 'write'],
    parts: ['read', 'write'],
    tasks: ['read', 'write'],
    reports: [],
    financials: [], // NO financials
    recon: ['read', 'write'],
    messages: ['read', 'write'],
  },
  detail: {
    vehicles: [], // Minimal access
    expenses: [],
    vendors: [],
    users: [],
    titles: [],
    inspections: [],
    parts: [],
    tasks: ['read', 'write'], // Only their own tasks
    reports: [],
    financials: [],
    recon: [],
    messages: [],
  },
  buyer: {
    vehicles: ['read', 'write', 'read_financials'],
    expenses: ['read', 'write'],
    vendors: ['read', 'write'],
    users: ['read'],
    titles: ['read', 'write'],
    inspections: ['read'],
    parts: ['read'],
    tasks: ['read', 'write'],
    reports: ['read'],
    financials: ['read', 'write'],
    recon: ['read'],
    messages: ['read', 'write'],
  },
};

// Which modules each role can access (in display order)
// Note: If user has allowed_modules set, that overrides these defaults
const ROLE_MODULES = {
  admin: ['dashboard', 'admintasks', 'analytics', 'accounting', 'dealdesk', 'dealdecklite', 'reports', 'titles', 'inventory', 'recon', 'workflow', 'reconworkflow', 'arb', 'partsroom', 'detail', 'transport', 'expenses', 'timeclock', 'vendors', 'alerts', 'admin', 'bhph'],
  office: ['dashboard', 'inventory', 'accounting', 'dealdesk', 'titles', 'arb', 'timeclock', 'bhph'],
  shop: ['recon', 'partsroom', 'transport', 'inventory', 'reconworkflow', 'expenses', 'timeclock'],
  tech: ['recon', 'partsroom', 'transport', 'inventory', 'timeclock'],
  detail: ['detail'],
  buyer: ['buyerdash', 'inventory', 'transport', 'titles', 'recon', 'arb', 'partsroom'],
};

// All available modules for custom permission assignment
export const ALL_MODULES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'accounting', label: 'Accounting' },
  { id: 'dealdesk', label: 'Deal Desk' },
  { id: 'reports', label: 'Reports' },
  { id: 'titles', label: 'Titles' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'recon', label: 'Recon' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'reconworkflow', label: 'Recon Workflow' },
  { id: 'arb', label: 'ARB' },
  { id: 'partsroom', label: 'Parts Room' },
  { id: 'detail', label: 'Detail' },
  { id: 'transport', label: 'Transport' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'timeclock', label: 'Time Clock' },
  { id: 'dealdecklite', label: 'Deal Desk Lite' },
  { id: 'bhph', label: 'BHPH Portfolio' },
];

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      // NOTE: the old localhost auto-admin bypass was removed — it faked a client-side
      // admin with no real token, so roles could never be tested. Local testing now uses
      // the dev quick-login buttons on the login screen (server must run with
      // ALLOW_DEV_LOGIN=true), which issue real tokens so permissions and financial
      // stripping behave exactly like production.

      const token = localStorage.getItem('lotsense_access_token');
      if (token) {
        try {
          const { user } = await api.getCurrentUser();
          setCurrentUser(user);
          setIsAuthenticated(true);
          // Restore last activity time or set to now
          const savedActivity = localStorage.getItem('lotsense_last_activity');
          if (savedActivity) {
            const timeSinceActivity = Date.now() - parseInt(savedActivity);
            if (timeSinceActivity > SESSION_TIMEOUT) {
              // Session expired, log out
              api.clearTokens();
              localStorage.removeItem('lotsense_last_activity');
              setCurrentUser(null);
              setIsAuthenticated(false);
            } else {
              setLastActivity(parseInt(savedActivity));
            }
          }
        } catch (error) {
          // Token invalid, clear it
          api.clearTokens();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Track user activity and auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      localStorage.setItem('lotsense_last_activity', now.toString());
    };

    // Events that indicate user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, updateActivity, { passive: true }));

    // Check for timeout every minute
    const checkTimeout = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivity;
      if (timeSinceActivity > SESSION_TIMEOUT) {
        console.log('[Security] Session timeout - logging out due to inactivity');
        api.clearTokens();
        localStorage.removeItem('lotsense_last_activity');
        setCurrentUser(null);
        setIsAuthenticated(false);
        alert('Your session has expired due to inactivity. Please log in again.');
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
      clearInterval(checkTimeout);
    };
  }, [isAuthenticated, lastActivity]);

  const login = useCallback(async (email, password) => {
    try {
      const { user } = await api.login(email, password);
      setCurrentUser(user);
      setIsAuthenticated(true);
      try {
        sessionStorage.setItem('lotsense_show_version_banner', '1');
      } catch {
        // ignore
      }
      return { success: true, user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // Passwordless persona login for local testing (server-gated by ALLOW_DEV_LOGIN)
  const devLogin = useCallback(async (persona) => {
    try {
      const { user } = await api.devLogin(persona);
      setCurrentUser(user);
      setIsAuthenticated(true);
      return { success: true, user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      setCurrentUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const isAdmin = useCallback(() => currentUser?.role === 'admin', [currentUser]);

  // Check if user has permission for a resource/action
  const hasPermission = useCallback((resource, action) => {
    if (!currentUser) return false;
    const permissions = ROLE_PERMISSIONS[currentUser.role] || {};
    const resourcePerms = permissions[resource] || [];
    return resourcePerms.includes(action);
  }, [currentUser]);

  // Check if user can see a specific module
  // Merges user's custom allowed_modules with role defaults so new modules show automatically
  const canAccess = useCallback((module) => {
    if (!currentUser) return false;
    const roleDefaults = ROLE_MODULES[currentUser.role] || [];
    if (currentUser.allowedModules && currentUser.allowedModules.length > 0) {
      return currentUser.allowedModules.includes(module) || roleDefaults.includes(module);
    }
    return roleDefaults.includes(module);
  }, [currentUser]);

  // Get user's modules in correct order for nav
  // Merges explicit allowedModules with role defaults (deduped)
  const getUserModules = useCallback(() => {
    if (!currentUser) return [];
    const roleDefaults = ROLE_MODULES[currentUser.role] || [];
    if (currentUser.allowedModules && currentUser.allowedModules.length > 0) {
      const merged = [...currentUser.allowedModules];
      roleDefaults.forEach(m => { if (!merged.includes(m)) merged.push(m); });
      return merged;
    }
    return roleDefaults;
  }, [currentUser]);

  // Check if user can see financial data
  const canSeeFinancials = useCallback(() => {
    if (!currentUser) return false;
    const perms = ROLE_PERMISSIONS[currentUser.role]?.vehicles || [];
    return perms.includes('read_financials');
  }, [currentUser]);

  // Get default module for role (for redirects)
  const getDefaultModule = useCallback(() => {
    if (!currentUser) return 'dashboard';
    const modules = ROLE_MODULES[currentUser.role] || ['dashboard'];
    return modules[0];
  }, [currentUser]);

  return (
    <AuthContext.Provider value={{
      currentUser,
      isAuthenticated,
      loading,
      login,
      devLogin,
      logout,
      isAdmin,
      hasPermission,
      canAccess,
      canSeeFinancials,
      getDefaultModule,
      getUserModules,
      permissions: currentUser ? ROLE_PERMISSIONS[currentUser.role] : {},
      modules: currentUser ? ROLE_MODULES[currentUser.role] : [],
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
